/**
 * Frequência de recorrência.
 *
 * O par `(frequency_type, frequency_number)` é a fonte da verdade — `frequency`
 * é só um rótulo derivado. Os presets ("Trimestral") são atalhos de UI que
 * preenchem o par; o backend deriva o mesmo par a partir do preset, então
 * enviá-lo é idempotente.
 *
 * Preferir sempre `frequency_description`, que o backend já entrega pronto.
 * `describeFrequency` existe como fallback para payloads que não o trazem.
 */

export type FrequencyUnit = 'days' | 'weeks' | 'months' | 'years'

/** Valores do enum `frequency` no backend. */
export enum FrequencyEnum {
  Semanal = 0,
  Mensal = 1,
  Trimestral = 2,
  Semestral = 3,
  Anual = 4,
  Quinzenal = 5,
  Bimestral = 6,
  Personalizada = 7
}

/** Par canônico de cada preset. Espelha `Recurrence::PRESET_TO_TYPE_NUMBER`. */
export const PRESET_TO_PAIR: Record<string, [FrequencyUnit, number]> = {
  weekly: ['weeks', 1],
  biweekly: ['weeks', 2],
  monthly: ['months', 1],
  bimonthly: ['months', 2],
  quarterly: ['months', 3],
  semester: ['months', 6],
  annual: ['years', 1]
}

/** Mesmo mapa, indexado pelo valor numérico do enum. */
export const FREQUENCY_ENUM_TO_NAME: Record<number, string> = {
  [FrequencyEnum.Semanal]: 'weekly',
  [FrequencyEnum.Mensal]: 'monthly',
  [FrequencyEnum.Trimestral]: 'quarterly',
  [FrequencyEnum.Semestral]: 'semester',
  [FrequencyEnum.Anual]: 'annual',
  [FrequencyEnum.Quinzenal]: 'biweekly',
  [FrequencyEnum.Bimestral]: 'bimonthly',
  [FrequencyEnum.Personalizada]: 'custom'
}

export const FREQUENCY_NAME_TO_ENUM: Record<string, number> = Object.entries(
  FREQUENCY_ENUM_TO_NAME
).reduce((acc, [value, name]) => ({ ...acc, [name]: Number(value) }), {})

const PRESET_LABEL: Record<string, string> = {
  weekly: 'Semanal',
  biweekly: 'Quinzenal',
  monthly: 'Mensal',
  bimonthly: 'Bimestral',
  quarterly: 'Trimestral',
  semester: 'Semestral',
  annual: 'Anual'
}

const UNIT_LABEL: Record<FrequencyUnit, [string, string]> = {
  days: ['dia', 'dias'],
  weeks: ['semana', 'semanas'],
  months: ['mês', 'meses'],
  years: ['ano', 'anos']
}

/**
 * Meses cobertos por UM ciclo do par canônico. Espelha
 * `CanonicalFrequency.months_per_cycle` (que usa Rational no Ruby; aqui basta Float,
 * porque a checagem de divisibilidade abaixo usa tolerância).
 *
 * Usa o mês comercial de 30 dias, então "a cada 90 dias" conta como 3 meses.
 */
export const monthsPerCycle = (unit: FrequencyUnit, count: number): number => {
  switch (unit) {
    case 'days':
      return count / 30
    case 'weeks':
      return count / 4
    case 'years':
      return count * 12
    default:
      return count
  }
}

/**
 * Família da unidade. Dias/semanas avançam por dias corridos; meses/anos avançam por
 * mês-calendário. São escalas incomensuráveis — nenhum número de semanas dá exatamente um
 * mês, em nenhum mês do ano.
 */
const FREQUENCY_FAMILY: Record<FrequencyUnit, 'short' | 'long'> = {
  days: 'short',
  weeks: 'short',
  months: 'long',
  years: 'long'
}

/**
 * Duração do ciclo na unidade natural da própria família: dias para days/weeks, meses para
 * months/years. As duas conversões são EXATAS (semana = 7 dias, ano = 12 meses), ao
 * contrário de `monthsPerCycle`, que usa o mês comercial de 30 dias.
 */
const cyclesInOwnUnit = (unit: FrequencyUnit, count: number): number =>
  unit === 'weeks' ? count * 7 : unit === 'years' ? count * 12 : count

/**
 * Passo (stride) de um item dentro de uma recorrência: quantos ciclos da recorrência cabem
 * em um ciclo do item. Espelha `CanonicalFrequency.cycle_interval_between`.
 *
 * A regra é: **as datas precisam coincidir**. Isso só acontece quando os dois contam na
 * mesma família e um é múltiplo inteiro do outro.
 *
 *   mensal + trimestral   => 3    (3 meses-calendário são exatamente um trimestre)
 *   a cada 15d + 45 dias  => 3    (45 dias corridos são exatamente 3 ciclos)
 *   quinzenal + mensal    => null (2 quinzenas são 28 dias; o plano promete um mês)
 *   mensal + 45 dias      => null (venceria entre dois ciclos)
 *
 * Comparar em "meses comerciais" fazia semanas e meses parecerem divisíveis e deixava passar
 * combinações cujas datas desviam. Com inteiros na mesma família a conta é exata — por isso
 * não há mais tolerância de ponto flutuante aqui.
 */
export const cycleIntervalBetween = (
  outer: [FrequencyUnit, number],
  inner: [FrequencyUnit, number]
): number | null => {
  if (FREQUENCY_FAMILY[outer[0]] !== FREQUENCY_FAMILY[inner[0]]) return null

  const outerCycles = cyclesInOwnUnit(outer[0], outer[1])
  const innerCycles = cyclesInOwnUnit(inner[0], inner[1])
  if (!outerCycles || outerCycles <= 0 || !innerCycles || innerCycles <= 0)
    return null

  const ratio = innerCycles / outerCycles
  return Number.isInteger(ratio) && ratio >= 1 ? ratio : null
}

/** Par canônico -> preset, ou `undefined` quando é uma combinação custom. */
export const pairToPreset = (
  unit?: string,
  count?: number
): string | undefined => {
  if (!unit || !count || count <= 0) return undefined
  return Object.keys(PRESET_TO_PAIR).find(
    (preset) =>
      PRESET_TO_PAIR[preset][0] === unit && PRESET_TO_PAIR[preset][1] === count
  )
}

/** Par canônico de um preset (ou o próprio par, quando já é custom). */
export const presetToPair = (
  frequency: string,
  unit?: FrequencyUnit,
  count?: number
): [FrequencyUnit, number] => {
  if (frequency === 'custom')
    return [unit ?? 'months', count && count > 0 ? count : 1]
  return PRESET_TO_PAIR[frequency] ?? ['months', 1]
}

/**
 * Rótulo humano derivado do par: o nome do preset quando o par bate com um
 * preset, senão "A cada N <unidade>". Espelha `Recurrence#frequency_description`.
 * Retorna string vazia quando a frequência é desconhecida, para o chamador poder
 * distinguir "sem rótulo" de um rótulo válido.
 */
export const describeFrequency = (
  frequency?: string,
  unit?: string,
  count?: number
): string => {
  if (!frequency) return ''

  const pair = PRESET_TO_PAIR[frequency]
  const resolvedUnit = (unit ?? pair?.[0]) as FrequencyUnit | undefined
  const resolvedCount = count && count > 0 ? count : pair?.[1]

  if (resolvedUnit && resolvedCount && UNIT_LABEL[resolvedUnit]) {
    const preset = pairToPreset(resolvedUnit, resolvedCount)
    if (preset) return PRESET_LABEL[preset]

    const [singular, plural] = UNIT_LABEL[resolvedUnit]
    return resolvedCount === 1
      ? `A cada ${singular}`
      : `A cada ${resolvedCount} ${plural}`
  }

  if (frequency === 'custom') return 'Personalizada'
  return PRESET_LABEL[frequency] ?? ''
}

/**
 * Cadência de um item da composição, para efeito de derivar a frequência da recorrência.
 * `key` só serve para o chamador identificar o item de volta (índice, uuid, o que for).
 */
export interface ItemCadence {
  key: string
  label: string
  pair: [FrequencyUnit, number]
}

export interface DerivedFrequency {
  /** Par da recorrência = cadência do item MAIS FREQUENTE. `null` quando não há item com plano. */
  pair: [FrequencyUnit, number] | null
  /** O item que ditou a frequência. */
  base?: ItemCadence
  /** Itens que não cabem num número inteiro de ciclos dessa base. */
  incompatible: ItemCadence[]
}

/**
 * Deriva a frequência da recorrência a partir dos itens: ela passa a ser a cadência do item
 * **mais frequente**, e os demais precisam ser múltiplos inteiros dela.
 *
 * Por que o mais frequente e não o MDC: o MDC pode cair numa frequência que não é de item
 * nenhum (mensal + 45 dias daria 15 dias), e aí existem ciclos em que NENHUM item é cobrado —
 * a recorrência gera ciclos vazios, o que quebra a própria noção de recorrência. Ancorando no
 * item mais frequente, esse item tem passo 1 e cobra em todo ciclo, por construção.
 */
export const deriveRecurrenceFrequency = (
  items: ItemCadence[]
): DerivedFrequency => {
  if (!items.length) return { pair: null, incompatible: [] }

  const base = items.reduce((menor, atual) =>
    monthsPerCycle(...atual.pair) < monthsPerCycle(...menor.pair)
      ? atual
      : menor
  )

  const incompatible = items.filter(
    (item) => cycleIntervalBetween(base.pair, item.pair) === null
  )

  return { pair: base.pair, base, incompatible }
}

/**
 * O candidato cabe na composição atual? Diferente de comparar com a frequência vigente: um
 * plano MAIS frequente que a atual é aceito e passa a ditar a nova frequência (escolher
 * semestral e depois mensal precisa funcionar, com a recorrência virando mensal).
 */
export const fitsInComposition = (
  current: ItemCadence[],
  candidate: ItemCadence
): boolean =>
  deriveRecurrenceFrequency([...current, candidate]).incompatible.length === 0
